import React from 'react'
import { useSelector } from 'react-redux'
import { z } from 'zod'

import Loader from '@/app/component/atom/loader'
import { Form } from '@/app/component/form'
import { ActionsContainer } from '@/app/component/form/common'
import SubmitButton from '@/app/component/form/submit-button'
import { Option } from '@/app/component/form/types'
import { useGetBalance } from '@/app/module/payments/hooks/use-get-balance'
import { useTransferFunds } from '@/app/module/payments/hooks/use-transfer-funds'
import { selectActiveOrg, selectOrgId, selectOrgs } from '@/app/module/user/store/selectors'

type Props = {
  onSuccess?: () => void
}

const TransferFunds: React.FC<Props> = ({ onSuccess }) => {
  const orgId = useSelector(selectOrgId)
  const orgs = useSelector(selectOrgs)
  const activeOrg = useSelector(selectActiveOrg)

  const { data: balance } = useGetBalance()

  const options = React.useMemo<Option[]>(() => {
    const list = (orgs as any[]) || []
    return list
      .filter((o) => o.orgId !== orgId)
      .map((o) => ({ label: `${o.name} (${o.orgId})`, value: String(o.orgId) }))
  }, [orgs, orgId])

  const defaultValues = React.useMemo<TransferFormValues>(
    () => ({ amount: 0, toOrgId: options?.[0]?.value || '' }),
    [options],
  )

  const [transfer, res] = useTransferFunds()
  const loading = res.isLoading

  const fields = React.useMemo(
    () =>
      ({
        toOrgId: {
          type: 'select' as const,
          fieldProps: {
            name: 'toOrgId',
            label: 'To organization',
            options,
            required: true,
          },
        },
        amount: {
          type: 'number' as const,
          fieldProps: {
            name: 'amount',
            label: 'Amount',
            required: true,
            autoFocus: true,
          },
        },
      }) as const,
    [options],
  )

  const handleSubmit = React.useCallback(
    async (values: TransferFormValues) => {
      const success = await transfer({
        amountUSDcent: Math.floor(values.amount * 100),
        toOrgId: Number(values.toOrgId),
      })
      if (success && onSuccess) {
        onSuccess()
      }
    },
    [transfer, onSuccess],
  )

  if (!activeOrg || !balance) {
    return <Loader />
  }

  return (
    <Form
      defaultValues={defaultValues}
      fields={fields}
      id="transfer-funds"
      name="transfer-funds"
      schema={getSchema(balance.balance)}
      shouldReset
      onSubmit={handleSubmit}
      renderActions={({ isDirty }) => (
        <ActionsContainer>
          <SubmitButton isDirty={isDirty} isSubmitting={loading}>
            Transfer funds
          </SubmitButton>
        </ActionsContainer>
      )}
    />
  )
}

const getSchema = (currentBalance: number) =>
  z.object({
    toOrgId: z.string().min(1, 'Select an organization'),
    amount: z
      .number({ invalid_type_error: 'Must be a number', required_error: 'Must be a number' })
      .gt(0, 'Amount must be greater than 0')
      .refine((v) => v <= currentBalance, {
        message: 'Insufficient funds',
      }),
  })

type TransferFormValues = z.infer<ReturnType<typeof getSchema>>

export default TransferFunds
